import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Total item count across all pages. */
  total: number;
  /** 1-based index of the first item shown. */
  from: number;
  /** 1-based index of the last item shown. */
  to: number;
  onPageChange: (page: number) => void;
  /** Singular noun for the range label, e.g. "worker" → "worker(s)". Read by
   *  the DEFAULT (English) range label only — see `PaginationLabels`. */
  itemLabel?: string;
  className?: string;
}

/* --- The words, so a tenant reading Arabic sees Arabic ---------------------
 *
 * This component hard-coded "Showing", "of", "Previous" and "Next", and
 * pluralised by appending an "s". That is three faults on a non-English
 * tenant: the chrome is untranslated, `${itemLabel}s` is not one of Arabic's
 * six plural forms, and the pager is the one control on a table that states
 * HOW MUCH of the data you are looking at. A screen that could not accept the
 * cost hand-rolled its own pager instead — `AttendancePager`, now deleted —
 * which is the second control language §6 exists to prevent.
 *
 * IT IS A CONTEXT, NOT SIX PROPS. Six label props would have put the same six
 * `t()` lookups at nineteen call sites, and a screen that got tired of copying
 * them is exactly how the private pager appeared the first time. The words
 * belong to the APP, not to the page: one provider near the root answers for
 * every pager in it, and a page that renders a pager writes nothing at all.
 *
 * `range` and `page` are FUNCTIONS taking the figures, not finished strings,
 * because the provider is mounted once and the numbers change per pager. The
 * other four are plain nodes.
 *
 * The default is today's English, spelled exactly as it was, so a `Pagination`
 * rendered with no provider above it — in a unit test, in the installer —
 * behaves as it always has and no caller breaks.
 * ------------------------------------------------------------------------ */
export interface PaginationLabels {
  /** "Showing 1–20 of 57". `itemLabel` is the caller's singular noun, which a
   *  translated label is free to ignore — the noun cannot be pluralised by a
   *  parameter in most languages, and the table's own caption already says
   *  what its rows are. */
  range: (r: {
    from: number;
    to: number;
    total: number;
    itemLabel: string;
  }) => ReactNode;
  /** "Page 2 of 5". */
  page: (r: { page: number; pageCount: number }) => ReactNode;
  /** Visible text on the two controls. */
  previous: ReactNode;
  next: ReactNode;
  /** Their accessible names, deliberately distinct from the visible words: a
   *  screen may carry a second "Previous" (a day stepper), and two controls
   *  with one accessible name are one control to anybody navigating by name. */
  previousAria: string;
  nextAria: string;
}

const DEFAULT_LABELS: PaginationLabels = {
  range: ({ from, to, total, itemLabel }) => (
    <>
      Showing{' '}
      <span className="font-medium text-foreground">
        {from}–{to}
      </span>{' '}
      of <span className="font-medium text-foreground">{total}</span>{' '}
      {total === 1 ? itemLabel : `${itemLabel}s`}
    </>
  ),
  page: ({ page, pageCount }) => `Page ${page} of ${pageCount}`,
  previous: 'Previous',
  next: 'Next',
  previousAria: 'Previous page',
  nextAria: 'Next page',
};

const PaginationLabelsContext = createContext<PaginationLabels>(DEFAULT_LABELS);

/** Mount once near the app root with the tenant's translated words. */
export function PaginationLabelsProvider({
  labels,
  children,
}: {
  labels: PaginationLabels;
  children: ReactNode;
}): JSX.Element {
  return (
    <PaginationLabelsContext.Provider value={labels}>
      {children}
    </PaginationLabelsContext.Provider>
  );
}

// Modelled on `<Button variant="outline" size="sm">` — kept as a local constant
// rather than the component itself so the pager stays free of a circular
// import and can size its two controls independently of the button scale. It
// now uses that independence for one property, and only one:
// `rounded-xs` (8px) and not the `rounded-control` (10px) the rest of the small
// controls take: the handoff's pager cap is its own shape — `min-height: 30px;
// padding: 0 9px; border-radius: 8px` (Hadir Employees.dc.html:325) — a tighter
// corner on a smaller cap. The 30px height is NOT copied; 32px is the floor the
// rest of the small controls sit at and a one-off 30px pager would be the only
// control in the app off that rail.
const btn = cn(
  'inline-flex min-h-8 items-center rounded-xs border border-border px-btn-x-sm py-btn-y-sm text-label text-foreground',
  'transition-colors duration-fast ease-standard hover:bg-muted',
  'disabled:pointer-events-none disabled:opacity-50',
  focusRing,
  '[@media(pointer:coarse)]:min-h-touch'
);

/**
 * Compact pager for client-paginated lists. Renders a "Showing X–Y of N"
 * range plus Previous / Next controls. Returns null when there's a single page
 * (nothing to page), so callers can drop it in unconditionally.
 *
 * Uses logical alignment (`justify-between`, `text-start`) so it mirrors under
 * RTL, and semantic tokens so it themes with light/dark.
 */
export function Pagination({
  page,
  pageCount,
  total,
  from,
  to,
  onPageChange,
  itemLabel = 'item',
  className,
}: PaginationProps): JSX.Element | null {
  const labels = useContext(PaginationLabelsContext);
  if (pageCount <= 1) return null;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-component border-t border-border py-cell-y',
        // `px-tile` (20px), not `px-cell-x` (16px): the pager band is the table
        // shell's own footer and the handoff insets it on the shell's rail —
        // `padding: 13px 20px` (Hadir Employees.dc.html:322) — so the range line
        // starts under the first column rather than 4px inside it.
        'px-tile',
        // The band carries its own type the way the mock's does: 12.5px in
        // `--foreground-tertiary` (#6A6659), which the two readouts below
        // inherit. `font-normal` because `text-label` is a 600 step and this is
        // a sentence, not a label — the only emphasis in the band belongs to the
        // figures, which set their own weight.
        'text-label font-normal text-foreground-tertiary',
        className
      )}
    >
      <p aria-live="polite">{labels.range({ from, to, total, itemLabel })}</p>
      <div className="flex items-center gap-element">
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={labels.previousAria}
        >
          {labels.previous}
        </button>
        <span className="px-1">{labels.page({ page, pageCount })}</span>
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label={labels.nextAria}
        >
          {labels.next}
        </button>
      </div>
    </div>
  );
}
